import { useState } from "react";
import { toast } from "sonner";
import { X, Download, Link2, Mail, Check, ExternalLink } from "lucide-react";
import { previewPdfUrl } from "@/lib/api";
import SendEmailDialog from "@/components/SendEmailDialog";

export default function HandoffDialog({ quote, emailStatus, onClose }) {
  const [showEmail, setShowEmail] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const shareUrl = `${window.location.origin}/q/${quote.share_token}`;
  const pdfHref = previewPdfUrl(quote.id);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      toast.success("Client link copied to clipboard");
      setTimeout(() => setLinkCopied(false), 2400);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  if (showEmail) {
    return (
      <SendEmailDialog
        quote={quote}
        emailStatus={emailStatus}
        onClose={() => setShowEmail(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="handoff-dialog">
      <div className="bg-white border border-[#050505] w-full max-w-2xl">
        <div className="border-b border-[#050505] px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-label mb-1">Quote saved</div>
            <h2 className="font-display font-bold text-2xl tracking-tight uppercase">
              {quote.quote_number} — Hand-off
            </h2>
            <div className="text-xs text-[#525252] mt-1.5">
              Choose how to deliver this quote to your client.
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#f5f5f5]" data-testid="handoff-close" aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-0">
          {/* Download PDF */}
          <a
            href={pdfHref}
            target="_blank"
            rel="noreferrer"
            className="p-6 sm:p-8 border-b sm:border-b-0 sm:border-r border-[#050505] hover:bg-[#f5f5f5] transition-colors group"
            data-testid="handoff-download-pdf"
          >
            <div className="w-10 h-10 bg-[#050505] text-white flex items-center justify-center mb-5 group-hover:bg-[#FF3333] transition-colors">
              <Download className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="font-display font-bold text-lg uppercase tracking-tight mb-1.5">
              Download PDF
            </div>
            <div className="text-xs text-[#525252] leading-relaxed">
              Save the client-view PDF locally to attach manually or print.
            </div>
            <div className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-bold">
              Open <ExternalLink className="w-3 h-3" strokeWidth={1.75} />
            </div>
          </a>

          {/* Copy share link */}
          <button
            onClick={copyLink}
            className="p-6 sm:p-8 border-b sm:border-b-0 sm:border-r border-[#050505] hover:bg-[#f5f5f5] transition-colors group text-left"
            data-testid="handoff-copy-link"
          >
            <div className="w-10 h-10 bg-[#050505] text-white flex items-center justify-center mb-5 group-hover:bg-[#FF3333] transition-colors">
              {linkCopied ? <Check className="w-5 h-5" strokeWidth={1.75} /> : <Link2 className="w-5 h-5" strokeWidth={1.5} />}
            </div>
            <div className="font-display font-bold text-lg uppercase tracking-tight mb-1.5">
              {linkCopied ? "Copied!" : "Copy Share Link"}
            </div>
            <div className="text-xs text-[#525252] leading-relaxed">
              Read-only client URL with Accept/Decline buttons.
            </div>
            <div className="mt-4 font-mono text-[10px] text-[#525252] truncate">
              /q/{quote.share_token}
            </div>
          </button>

          {/* Email */}
          <button
            onClick={() => setShowEmail(true)}
            className="p-6 sm:p-8 hover:bg-[#f5f5f5] transition-colors group text-left"
            data-testid="handoff-email"
          >
            <div className="w-10 h-10 bg-[#FF3333] text-white flex items-center justify-center mb-5 group-hover:bg-[#050505] transition-colors">
              <Mail className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="font-display font-bold text-lg uppercase tracking-tight mb-1.5">
              Email to Client
            </div>
            <div className="text-xs text-[#525252] leading-relaxed">
              Send the PDF + link in a branded email — recipient can Accept online.
            </div>
            <div className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-bold text-[#FF3333]">
              {emailStatus?.configured ? "Send now" : "Setup required"}
            </div>
          </button>
        </div>

        <div className="border-t border-[#050505] px-6 py-4 flex justify-between items-center">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#525252]">
            Language:&nbsp;
            <span className="font-mono normal-case tracking-normal">
              {quote.language === "fr" ? "Français" : "English"}
            </span>
          </div>
          <a
            href={`/quotes/${quote.id}`}
            className="text-[11px] uppercase tracking-[0.2em] font-bold ghost-link inline-flex items-center gap-1"
            data-testid="handoff-open-detail"
          >
            View full quote <ExternalLink className="w-3 h-3" strokeWidth={1.75} />
          </a>
        </div>
      </div>
    </div>
  );
}
